import { Reveal } from "../Reveal";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { ArrowRight, Mail } from "lucide-react";
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaWhatsapp } from "react-icons/fa";
import { NAV_LINKS } from "../../data/content";
import { OPEN_EVENT } from "../../lib/consent";

// Same official profiles as the Contact page.
const SOCIAL_LINKS = [
  { icon: Mail, label: "Email", href: "mailto:contact@magicbolt.ai", color: "#FF6B00", title: "contact@magicbolt.ai" },
  { icon: FaFacebookF, label: "Facebook", href: "https://www.facebook.com/magicboltai/", color: "#1877F2" },
  { icon: FaInstagram, label: "Instagram", href: "https://www.instagram.com/magicboltai/", color: "#E1306C" },
  { icon: FaLinkedinIn, label: "LinkedIn", href: "https://www.linkedin.com/in/magicbolt/", color: "#0A66C2" },
  { icon: FaWhatsapp, label: "WhatsApp", href: "https://chat.whatsapp.com/KrxiKExv2Au8kXfvBRMfM9", color: "#25D366" },
];

function IndiaFlag({ className = "" }) {
  return (
    <svg viewBox="0 0 30 20" className={className} role="img" aria-label="Indian flag">
      <rect width="30" height="20" fill="#FFFFFF" />
      <rect width="30" height="6.67" fill="#FF9933" />
      <rect y="13.33" width="30" height="6.67" fill="#138808" />
      <circle cx="15" cy="10" r="2.6" fill="none" stroke="#000080" strokeWidth="0.6" />
      <circle cx="15" cy="10" r="0.5" fill="#000080" />
    </svg>
  );
}

export default function Footer({ showCta = true }) {
  return (
    <footer data-testid="footer-section" className="relative border-t border-[#E6E9F1] bg-[#F4F6FB]">
      {/* CTA */}
      {showCta && (
        <div className="relative overflow-hidden">
          <div className="mb-blob mb-blob-orange mb-anim-a w-[360px] h-[360px] -top-20 left-1/2 -translate-x-1/2" />
          <div className="absolute inset-0 mb-grid-lines opacity-60" />
          <div className="absolute inset-0 mb-radial-glow" />
          <div className="relative max-w-4xl mx-auto px-6 py-24 md:py-32 text-center">
            <Reveal>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-none text-[#0B1533]">
                Start Your <span className="mb-gradient-text">Recurring Revenue</span> Business Today
              </h2>
              <p className="mt-5 text-base md:text-lg text-[#475569] max-w-xl mx-auto leading-relaxed">
                Stop billing your best hours to busywork. Put MagicBolt AI to work and scale
                your agency without scaling your team.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  data-testid="footer-cta"
                  size="lg"
                  className="bg-[#FF6B00] hover:bg-[#FF8533] text-white font-semibold rounded-lg h-12 px-7 text-base hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(255,107,0,0.35)]"
                  asChild
                >
                  <Link to="/agency-os">
                    Get Agency OS <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-[#CBD2E0] bg-white text-[#0B1533] hover:bg-white hover:text-[#0B1533] font-semibold rounded-lg h-12 px-7 text-base hover:-translate-y-0.5"
                  asChild
                >
                  <Link to="/features">Explore Features</Link>
                </Button>
              </div>

              <div className="mt-10 flex flex-wrap items-center justify-center gap-x-5 gap-y-4">
                <div data-testid="footer-social-links" className="flex items-center justify-center gap-3">
                  {SOCIAL_LINKS.map((s) => (
                    <a
                      key={s.label}
                      href={s.href}
                      {...(s.href.startsWith("mailto:") ? {} : { target: "_blank", rel: "noopener noreferrer" })}
                      aria-label={s.title ? `Email ${s.title}` : `MagicBolt AI on ${s.label}`}
                      title={s.title || s.label}
                      className="grid h-11 w-11 place-items-center rounded-full border border-[#E6E9F1] bg-white shadow-[0_2px_10px_rgba(11,21,51,0.05)] transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[0_10px_24px_rgba(11,21,51,0.12)]"
                    >
                      <s.icon className="h-[18px] w-[18px]" style={{ color: s.color }} />
                    </a>
                  ))}
                </div>
                <span aria-hidden className="hidden h-6 w-px bg-[#CBD2E0] sm:block" />
                <div
                  data-testid="footer-made-in-india"
                  className="inline-flex items-center gap-2.5 h-11 rounded-full border border-[#E6E9F1] bg-white px-4 text-sm font-semibold text-[#0B1533] shadow-[0_2px_10px_rgba(11,21,51,0.05)]"
                >
                  <IndiaFlag className="h-4 w-6 rounded-[3px] ring-1 ring-[#E6E9F1]" />
                  Made in India
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      )}

      {/* Bottom bar */}
      <div className="border-t border-[#E6E9F1] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/" className="flex items-center">
            <img
              src="/magicbolt-logo-white-bg.webp"
              alt="MagicBolt AI"
              width="720"
              height="240"
              loading="lazy"
              decoding="async"
              className="h-16 w-auto object-contain"
            />
          </Link>

          <nav className="flex flex-wrap items-center justify-center gap-6">
            {NAV_LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                className="text-sm text-[#475569] hover:text-[#0B1533] transition-colors"
              >
                {l.label}
              </a>
            ))}
            <a href="/white-label-ai-saas-lifetime-deal" className="text-sm text-[#475569] hover:text-[#0B1533] transition-colors">
              Lifetime Deal
            </a>
            <a href="/product-finder" className="text-sm text-[#475569] hover:text-[#0B1533] transition-colors">
              Product Finder
            </a>
            <a href="/faq" className="text-sm text-[#475569] hover:text-[#0B1533] transition-colors">
              FAQ
            </a>
            <a href="/about" className="text-sm text-[#475569] hover:text-[#0B1533] transition-colors">
              About
            </a>
            <a href="/contact" className="text-sm text-[#475569] hover:text-[#0B1533] transition-colors">
              Contact
            </a>
          </nav>

          <p className="text-sm text-[#64748B]">
            © {new Date().getFullYear()} MagicBolt AI. All rights reserved.
          </p>
        </div>

        <div className="border-t border-[#E6E9F1]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-8 py-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <Link to="/privacy-policy" className="text-xs text-[#64748B] hover:text-[#0B1533] transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-conditions" className="text-xs text-[#64748B] hover:text-[#0B1533] transition-colors">
              Terms & Conditions
            </Link>
            <Link to="/refund-policy" className="text-xs text-[#64748B] hover:text-[#0B1533] transition-colors">
              Refund Policy
            </Link>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event(OPEN_EVENT))}
              data-testid="footer-cookie-settings"
              className="text-xs text-[#64748B] hover:text-[#0B1533] transition-colors"
            >
              Cookie settings
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
